import { useId, useState } from "react";
import type { MonthViewModel } from "../../domain/views.ts";
import { monthPieData, spendingPieData, type MonthPieMode } from "../monthPie.ts";
import { useMoneyFormat } from "../hooks/useMoneyFormat.ts";
import { Section, SectionToggle } from "./Section.tsx";
import { Segmented } from "./Segmented.tsx";
import { useDisplayPreference } from "../hooks/useDisplayPreference.ts";
import { useDataset } from "../hooks/useDataset.ts";
import { Button } from "@/components/ui/button";

const COLORS = ["#0d6b60", "#cc8050", "#537ba8", "#9a6bb1", "#9b8b39", "#ca6574", "#4a9b93", "#816eaf"];
const OPTIONS = [
  { value: "spending", label: "Spent" },
  { value: "allocation", label: "Allocated" },
] as const;

function point(degrees: number): [number, number] {
  const radians = ((degrees - 90) * Math.PI) / 180;
  return [100 + 88 * Math.cos(radians), 100 + 88 * Math.sin(radians)];
}

function wedge(start: number, end: number): string {
  const [startX, startY] = point(start);
  const [endX, endY] = point(end);
  return `M 100 100 L ${startX} ${startY} A 88 88 0 ${end - start > 180 ? 1 : 0} 1 ${endX} ${endY} Z`;
}

export function MonthPieShowcase({ view, baseCurrency }: { view: MonthViewModel; baseCurrency: string }) {
  const [mode, setMode] = useState<MonthPieMode>("spending");
  const [visible, setVisible] = useDisplayPreference("budget2:show-month-breakdown", true);
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(() => new Set());
  const contentId = useId();
  const dataset = useDataset();
  const fmt = useMoneyFormat();
  const spending = spendingPieData(dataset, view.monthId, excluded);
  const chart = mode === "spending" ? spending.chart : monthPieData(view, mode);
  // Reserve colors before filtering or sorting, so selection never recolors a post.
  const postColors = new Map(dataset.posts.map((post, index) => [post.id, COLORS[index % COLORS.length]!]));
  const purchaseColors = new Map(dataset.purchases.map((purchase, index) => [purchase.id, COLORS[index % COLORS.length]!]));
  const postColor = (id: string) => id === "unassigned"
    ? COLORS[dataset.posts.length % COLORS.length]!
    : postColors.get(id)!;
  const sliceColor = (id: string) => mode === "allocation" ? postColor(id)
    : spending.focusLabel ? purchaseColors.get(id)! : postColor(id === "unassigned" ? id : id.slice("post:".length));
  let angle = 0;
  const label = mode === "spending" ? "spending" : "allocation";

  return (
    <Section
      title="Monthly breakdown"
      hint={visible ? mode === "spending" ? "Choose posts to compare. Select one to see its purchases. Unchecking a post changes this chart only." : "Share of this month's allocations by post." : undefined}
      action={<>
        {visible && <Segmented value={mode} options={OPTIONS} onChange={setMode} label="Pie chart view" />}
        <SectionToggle title="Monthly breakdown" expanded={visible} contentId={contentId} onToggle={() => setVisible(!visible)} />
      </>}
    >
      <div id={contentId}>
      {visible && <div className="grid items-start gap-5 sm:grid-cols-[minmax(12rem,16rem)_1fr] sm:gap-8">
        <div className="mx-auto w-full max-w-56">
          <svg viewBox="0 0 200 200" role="img" aria-label={`${label} pie chart`} className="aspect-square w-full">
            {chart.positiveTotal === 0 ? (
              <circle cx="100" cy="100" r="88" fill="var(--budget-tint)" />
            ) : chart.positive.length === 1 ? (
              <circle cx="100" cy="100" r="88" fill={sliceColor(chart.positive[0]!.id)}>
                <title>{`${chart.positive[0]!.label}: ${fmt.money(chart.positive[0]!.amount, baseCurrency)}`}</title>
              </circle>
            ) : chart.positive.map((item, index) => {
              const start = angle;
              angle += (item.amount / chart.positiveTotal) * 360;
              return (
                <path
                  key={item.id}
                  d={wedge(start, index === chart.positive.length - 1 ? 360 : angle)}
                  fill={sliceColor(item.id)}
                  stroke="var(--budget-raised)"
                  strokeWidth="2"
                >
                  <title>{`${item.label}: ${fmt.money(item.amount, baseCurrency)}`}</title>
                </path>
              );
            })}
          </svg>
        </div>
        <div className="min-w-0">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1 border-b border-budget-rule pb-3">
            <span className="text-sm text-budget-ink-muted">{mode === "spending" ? spending.selectedCount === spending.posts.length ? "Net spent" : "Selected net spent" : "Total allocated"}</span>
            <strong className="font-money text-xl font-medium tabular-nums">{fmt.money(chart.netTotal, baseCurrency)}</strong>
          </div>
          {mode === "spending" && spending.posts.length > 0 && <fieldset className="mb-4">
            <legend className="sr-only">Posts included in spending chart</legend>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs text-budget-ink-muted">{spending.selectedCount} of {spending.posts.length} selected</span>
              <div className="flex gap-1">
                <Button size="xs" variant="ghost" onClick={() => setExcluded(new Set())}>Select all</Button>
                <Button size="xs" variant="ghost" onClick={() => setExcluded(new Set(spending.posts.map(post => post.id)))}>Clear</Button>
              </div>
            </div>
            <div className="space-y-1">
              {spending.posts.map(post => <label key={post.id} className="flex min-h-8 cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" aria-label={`Include ${post.label}`} checked={!excluded.has(post.id)} className="size-4 shrink-0 accent-primary" onChange={event => {
                  const checked = event.target.checked;
                  setExcluded(previous => {
                    const next = new Set(previous);
                    if (checked) next.delete(post.id); else next.add(post.id);
                    return next;
                  });
                }} />
                {!spending.focusLabel && !excluded.has(post.id) && post.amount > 0 && <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: sliceColor(post.id) }} aria-hidden="true" />}
                <span className="min-w-0 flex-1 break-words">{post.label}</span>
                <span className="font-money shrink-0 tabular-nums">{fmt.money(post.amount, baseCurrency)}
                  {!spending.focusLabel && !excluded.has(post.id) && post.amount > 0 && <span className="ml-1 text-xs text-budget-ink-muted">{Math.round(post.amount / chart.positiveTotal * 100)}%</span>}
                </span>
              </label>)}
            </div>
          </fieldset>}
          {mode === "spending" && spending.focusLabel && <h3 className="mb-2 border-t border-budget-rule pt-3 text-sm">Purchases in {spending.focusLabel}</h3>}
          {chart.positive.length === 0 ? (
            <p className="py-5 text-sm text-budget-ink-muted">
              {mode === "spending" ? spending.posts.length > 0 && spending.selectedCount === 0 ? "No posts selected. Tick a post or select all to show spending." : "No positive spending to chart." : "No positive allocations to chart."}
            </p>
          ) : mode === "allocation" || spending.focusLabel ? (
            <ol className="space-y-2.5">
              {chart.positive.map((item) => (
                <li key={item.id} className="grid grid-cols-[1rem_minmax(0,1fr)_auto] items-baseline gap-x-2.5 text-sm">
                  <span className="inline-block size-2.5 self-center rounded-full" style={{ backgroundColor: sliceColor(item.id) }} aria-hidden="true" />
                  <span className="min-w-0 truncate" title={item.label}>{item.label}</span>
                  <span className="font-money text-right tabular-nums whitespace-nowrap">{fmt.money(item.amount, baseCurrency)} <span className="ml-1 text-xs text-budget-ink-muted">{Math.round((item.amount / chart.positiveTotal) * 100)}%</span></span>
                </li>
              ))}
            </ol>
          ) : null}
          {chart.adjustments.length > 0 && (
            <div className="mt-4 border-t border-budget-rule pt-3">
              <p className="mb-2 text-xs text-budget-ink-muted">Refunds and negative adjustments are excluded from pie slices.</p>
              <ul className="space-y-1.5 text-sm">
                {chart.adjustments.map((item) => (
                  <li key={item.id} className="flex justify-between gap-3">
                    <span className="min-w-0 truncate" title={item.label}>{item.label}</span>
                    <span className="font-money shrink-0 tabular-nums">{fmt.money(item.amount, baseCurrency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>}
      </div>
    </Section>
  );
}
